import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Card, CircularProgress, Alert, Button, Divider,
  Dialog, DialogTitle, DialogContent, IconButton, Collapse, Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import WorkflowService, { WorkflowInstance, WorkflowInstanceStep } from '../../api/services/workflow.service';
import { useAuthStore } from '../../store/authStore';
import {
  instanceTone, buildPulse, PulseRow, stepDotColor, stepTone, operationDay, operationTone,
} from './workflowUI';
import PageHeader from '../../components/ui/PageHeader';
import PageBody, { READING_WIDTH } from '../../components/ui/PageBody';
import ToneChip from '../../components/ui/ToneChip';
import { LoadingState } from '../../components/ui/States';
import { NavPanel, PanelGroup } from '../../components/layout/NavPanel';
import { usePageBreadcrumb } from '../../components/layout/Breadcrumb';
import StepAnswer from './StepAnswer';
import OperationDays from './OperationDays';

const OperationDetail: React.FC = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const { account, canArea } = useAuthStore();
  const canCancel = account?.role === 'SA' || canArea('operations', 'manage');

  const [inst, setInst] = useState<WorkflowInstance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  // Clicking any step (at any status) opens its full details.
  const [openStep, setOpenStep] = useState<WorkflowInstanceStep | null>(null);
  const [expandedSkips, setExpandedSkips] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    if (!instanceId) return;
    setLoading(true);
    try {
      setError(null);
      setInst(await WorkflowService.getInstance(instanceId));
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Could not load this workflow');
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => { load(); }, [load]);

  const doCancel = async () => {
    if (!inst) return;
    if (!window.confirm(`Cancel "${inst.label}"? It will stop and move to Done as cancelled.`)) return;
    setCancelling(true);
    try {
      const updated = await WorkflowService.cancelInstance(inst._id);
      setInst(updated);
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Could not cancel');
    } finally {
      setCancelling(false);
    }
  };

  const stepIcon = (step: WorkflowInstanceStep) => {
    const c = stepDotColor(step);
    switch (step.status) {
      case 'completed': return <CheckCircleIcon sx={{ color: c }} />;
      case 'active': return <PendingIcon sx={{ color: c }} />;
      case 'escalated':
      case 'failed': return <ErrorIcon sx={{ color: c }} />;
      case 'skipped': return <RemoveCircleOutlineIcon sx={{ color: c }} />;
      default: return <RadioButtonUncheckedIcon sx={{ color: c }} />;
    }
  };

  // Walk steps in order (conditionals hidden); runs of skipped steps
  // collapse into one inline row AT THEIR POSITION.
  type Row = { kind: 'step'; step: WorkflowInstanceStep } | { kind: 'skipped'; steps: WorkflowInstanceStep[] };
  const rows = useMemo<Row[]>(() => {
    const nonConditional = (inst?.steps || []).filter((s) => s.type !== 'conditional');
    const out: Row[] = [];
    for (let i = 0; i < nonConditional.length; ) {
      if (nonConditional[i].status === 'skipped') {
        const group: WorkflowInstanceStep[] = [];
        while (i < nonConditional.length && nonConditional[i].status === 'skipped') { group.push(nonConditional[i]); i++; }
        out.push({ kind: 'skipped', steps: group });
      } else {
        out.push({ kind: 'step', step: nonConditional[i] });
        i++;
      }
    }
    return out;
  }, [inst]);
  const pulse = useMemo(() => (inst ? buildPulse([inst], 30) : []), [inst]);

  // Declared here rather than in the render body: it is a hook, and the
  // loading and error branches below return early.
  usePageBreadcrumb(inst ? [{ label: inst.label }] : []);

  if (loading) return <LoadingState label="Loading this run…" height={400} />;
  if (error || !inst) {
    return (
      <PageBody>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Workflow not found'}</Alert>
        <Button variant="outlined" onClick={() => navigate('/operations')}>Back to Operations</Button>
      </PageBody>
    );
  }

  const isOperation = inst.kind === 'operation';
  const tone = isOperation ? operationTone(inst, (inst.children || []).find((c) => c.status === 'active')) : instanceTone(inst);
  const elapsedMs = (inst.completedAt ? new Date(inst.completedAt).getTime() : Date.now()) - new Date(inst.startedAt).getTime();
  const elapsed = elapsedMs < 3600000 ? `${Math.round(elapsedMs / 60000)}m` : `${(elapsedMs / 3600000).toFixed(1)}h`;

  return (
    <>
      {/* The run's own facts live in the panel, so the timeline gets the
          full width of the page instead of sharing it with a meta column. */}
      <NavPanel>
        <PanelGroup label={isOperation ? 'This operation' : 'This run'}>
          <Box sx={{ px: 1.5 }}>
            <Box sx={{ mb: 1.5 }}><ToneChip tone={tone.tone} label={tone.label} /></Box>
            <MetaRow k="Template" v={inst.templateName} />
            <MetaRow k="Origin" v={inst.triggerType === 'manual' ? 'Started by hand' : `${inst.triggerType}-triggered`} />
            <MetaRow k="Area" v={inst.serviceAreaId != null ? `Area ${inst.serviceAreaId}` : '—'} />
            <MetaRow k="Started" v={new Date(inst.startedAt).toLocaleString()} />
            <MetaRow k={isOperation ? 'Day' : 'Elapsed'} v={isOperation ? String(operationDay(inst)) : elapsed} />

            {/* The run is about somebody. The work (a call, a message, a
                stage change) happens on their record, so that is where
                this goes; the run only keeps score. */}
            {inst.context?.candidateId && (
              <Button fullWidth variant="contained" disableElevation sx={{ mt: 2 }} onClick={() => navigate(`/hiring/${inst.context!.candidateId}`)}>
                Open {inst.context?.name || 'the candidate'}
              </Button>
            )}
            {inst.context?.ownerType && inst.context?.ownerId && (
              <Button fullWidth variant="contained" disableElevation sx={{ mt: 2 }} onClick={() => navigate(`/files/entity/${inst.context!.ownerType}/${inst.context!.ownerId}`)}>
                Open {inst.context?.ownerLabel || 'the file'}
              </Button>
            )}

            {inst.context?.traineeAccountId && (
              <Button fullWidth variant="contained" disableElevation sx={{ mt: 2 }} onClick={() => navigate(`/users/${inst.context!.traineeAccountId}`)}>
                Open {inst.context?.name || 'their page'}
              </Button>
            )}
            {inst.parentId && (
              <Button fullWidth variant="outlined" sx={{ mt: 2 }} onClick={() => navigate(`/operations/${inst.parentId}`)}>
                Part of an ongoing operation
              </Button>
            )}

            {/* An operation ends when what it follows ends (a training is
                finished on the day or on the person's page), not here. */}
            {canCancel && inst.status === 'active' && !isOperation && (
              <Button
                fullWidth variant="outlined" color="error" disabled={cancelling}
                sx={{ mt: 2 }}
                onClick={doCancel}
              >
                {cancelling ? 'Cancelling…' : 'Cancel workflow'}
              </Button>
            )}
          </Box>
        </PanelGroup>

        <PanelGroup label="Engine activity">
          <Box sx={{ px: 1.5 }}>
            {pulse.length === 0
              ? <Typography variant="caption" color="text.disabled">Nothing yet.</Typography>
              : <Box sx={{ maxHeight: 340, overflowY: 'auto' }}>{pulse.map((e, i) => <PulseRow key={i} e={e} />)}</Box>}
          </Box>
        </PanelGroup>
      </NavPanel>

      <PageBody>
        <PageHeader
          title={inst.label}
          subtitle={<Stack direction="row" alignItems="center" gap={1} component="span">
            <ToneChip tone={tone.tone} label={tone.label} />
            <span>{inst.templateName}</span>
          </Stack>}
          actions={
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
          }
        />

        {(() => {
          const waiting = inst.status === 'active'
            ? inst.steps.find((s) => s.stepId === inst.currentStepId && s.type === 'human_task' && (s.status === 'active' || s.status === 'escalated'))
            : undefined;
          return waiting ? <Box sx={{ maxWidth: READING_WIDTH }}><StepAnswer inst={inst} step={waiting} onDone={setInst} /></Box> : null;
        })()}

        {isOperation ? (
          <Box sx={{ maxWidth: READING_WIDTH }}><OperationDays inst={inst} onChanged={load} /></Box>
        ) : (
        <Card sx={{ p: 2, maxWidth: READING_WIDTH }}>
            {rows.map((row, ri) => {
              const last = ri === rows.length - 1;

              if (row.kind === 'skipped') {
                const key = row.steps[0].stepId;
                const open = !!expandedSkips[key];
                return (
                  <Box key={`skip-${key}`}>
                    <Box
                      onClick={() => setExpandedSkips((m) => ({ ...m, [key]: !m[key] }))}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, px: 1, mx: -1, cursor: 'pointer', color: 'text.secondary', borderRadius: 1, '&:hover': { bgcolor: 'grey.50' } }}
                    >
                      <RemoveCircleOutlineIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                      <Typography variant="body2" sx={{ flex: 1, color: 'text.secondary' }}>
                        {row.steps.length === 1 ? `Skipped: ${row.steps[0].title}` : `${row.steps.length} steps skipped`}
                      </Typography>
                      <ExpandMoreIcon sx={{ fontSize: 18, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
                    </Box>
                    <Collapse in={open}>
                      <Box sx={{ pl: 4.5, pb: 1 }}>
                        {row.steps.map((step) => (
                          <Box
                            key={step.stepId}
                            onClick={() => setOpenStep(step)}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, cursor: 'pointer', color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
                          >
                            <Typography variant="body2" sx={{ flex: 1 }}>{step.title}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>Details ›</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                    {!last && <Divider />}
                  </Box>
                );
              }

              const step = row.step;
              return (
                <Box key={step.stepId}>
                  <Box
                    onClick={() => setOpenStep(step)}
                    sx={{
                      display: 'flex', gap: 2, py: 1.5, alignItems: 'flex-start', cursor: 'pointer',
                      borderRadius: 1, mx: -1, px: 1, '&:hover': { bgcolor: 'grey.50' },
                    }}
                  >
                    <Box sx={{ mt: 0.25 }}>{stepIcon(step)}</Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: step.status === 'active' ? 700 : 500, color: 'text.primary' }}>
                        {step.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        {step.status}
                        {step.assignedRole ? ` · ${step.assignedRole}` : ''}
                        {step.completedAt ? ` · ${new Date(step.completedAt).toLocaleString()}` : ''}
                        {step.status === 'active' && step.dueAt ? ` · due ${new Date(step.dueAt).toLocaleTimeString()}` : ''}
                      </Typography>
                      {step.reason && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{step.reason}</Typography>
                      )}
                      {(step.proofPhotos?.length ?? 0) > 0 && (
                        <Typography variant="caption" sx={{ color: 'primary.main', display: 'block' }}>
                          📷 {step.proofPhotos!.length} photo{step.proofPhotos!.length > 1 ? 's' : ''}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>Details ›</Typography>
                  </Box>
                  {!last && <Divider />}
                </Box>
              );
            })}
        </Card>
        )}

      {/* Read-only step details — works at any status, including done. */}
      <Dialog open={!!openStep} onClose={() => setOpenStep(null)} maxWidth="sm" fullWidth>
        {openStep && (
          <>
            <DialogTitle sx={{ pr: 6 }}>
              {openStep.title}
              <IconButton onClick={() => setOpenStep(null)} sx={{ position: 'absolute', right: 8, top: 8 }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 1.5 }}>
                <ToneChip tone={stepTone(openStep)} label={openStep.status} />
              </Box>
              {openStep.description && (
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>{openStep.description}</Typography>
              )}
              <MetaRow k="Assigned role" v={openStep.assignedRole || '—'} />
              {openStep.reason && <MetaRow k="What happened" v={openStep.reason} />}
              {openStep.startedAt && <MetaRow k="Started" v={new Date(openStep.startedAt).toLocaleString()} />}
              {openStep.dueAt && <MetaRow k="Due" v={new Date(openStep.dueAt).toLocaleString()} />}
              {openStep.completedAt && <MetaRow k="Completed" v={new Date(openStep.completedAt).toLocaleString()} />}
              {openStep.notes && <MetaRow k="Notes" v={openStep.notes} />}

              {(openStep.proofPhotos?.length ?? 0) > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 1 }}>
                    Proof photos
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {openStep.proofPhotos!.map((p, i) => <ProofPhoto key={i} path={p} />)}
                  </Box>
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
      </PageBody>
    </>
  );
};

// Resolves a stored proof-photo path to a short-lived signed URL. Legacy
// local-device URIs (from before upload was wired) can't be viewed.
const ProofPhoto: React.FC<{ path: string }> = ({ path }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    if (/^https?:\/\//.test(path)) { setUrl(path); return; }
    if (!path.startsWith('workflow_photos/')) { setFailed(true); return; }
    WorkflowService.getPhotoUrl(path).then((u) => { if (alive) { if (u) setUrl(u); else setFailed(true); } });
    return () => { alive = false; };
  }, [path]);

  if (failed) {
    return (
      <Box sx={{ width: 96, height: 96, borderRadius: 2, border: '1px dashed', display: 'grid', placeItems: 'center', color: 'text.disabled', fontSize: 11, textAlign: 'center', p: 1 }}>
        not viewable
      </Box>
    );
  }
  if (!url) {
    return <Box sx={{ width: 96, height: 96, borderRadius: 2, bgcolor: 'grey.50', display: 'grid', placeItems: 'center' }}><CircularProgress size={18} /></Box>;
  }
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img src={url} alt="proof" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid #E5E4E0' }} />
    </a>
  );
};

const MetaRow: React.FC<{ k: string; v: string }> = ({ k, v }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>{k}</Typography>
    <Typography variant="caption" sx={{ color: 'text.primary', textAlign: 'right' }}>{v}</Typography>
  </Box>
);

export default OperationDetail;
